import {noop} from '@benchling/js-utils';
import {Box, createStyles, useMantineTheme} from '@mantine/core';
import produce from 'immer';
import React, {useMemo, useRef} from 'react';

import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import useHydratedStructuredTableData from 'notebook/structured-tables/hooks/useHydratedStructuredTableData';
import useLocalStorageColumnWidthStore from 'notebook/structured-tables/hooks/useLocalStorageColumnWidthStore';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {TableValidationCellError} from 'notebook/structured-tables/StructuredTableApiConstants';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableContext from 'notebook/structured-tables/util/StructuredTableContext';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {RowData} from 'notebook/tables/constants/RowConstants';
import {ColumnConstraint} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import MediocreWrapper from 'notebook/tables/core-ui/MediocreWrapper';
import {TableEditableProps} from 'notebook/tables/core-ui/TableEditableProps';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {getTextDimensions} from 'util/getTextDimensions';

import {columnIndexByUuid, TransposePivotSpec} from './TransposePivot';

/**
 * Generic "pivot mode" that hosts the real notebook table engine (`SimpleTableEditable` /
 * `TableEditable`) over a PIVOTED view of a flat structured table.
 *
 * A `TransposePivotSpec` (see `TransposePivot`) supplies all table-type-specific knowledge: which
 * canonical column is projected into the cells, and how to classify each canonical row into transposed
 * (row, column) coordinates + labels. This component knows nothing about parameters, PMEIs, or any
 * specific column — it only reshapes and hosts.
 *
 * Because the engine re-evaluates the synthetic `Table` we feed it, entity chips, selector/measurement
 * editors, cell error decorations, range selection, fill-down and paste all work natively — we just
 * translate the engine's edits back onto the canonical rows in `updateHandler`.
 *
 * Bijection:
 *  - forward: canonical `rows[classified].cells[projected]` -> synthetic `table[row][column]`. Per-row
 *    data types are carried via a ROW_DELEGATE constraint per synthetic column, with the concrete
 *    constraint stored in `rowData[row].delegatedColumnConstraints`.
 *  - back: each edited synthetic cell is mapped to its canonical row and written in place (typedValue +
 *    text only, never hydrated UI fields), avoiding a positional `fromTable` round-trip.
 *
 * The row-label axis becomes the engine's frozen `rowLabels` rather than a synthetic data column.
 */

interface TransposedStructuredTableEditableProps {
  /** The flat structured table to render transposed. */
  structuredTable: UnknownStructuredTable;
  /** Supplies the projected column and row-classification logic for this table type. */
  pivotSpec: TransposePivotSpec;
  /**
   * When true the grid is read-only. Mirrors the normal view's `tableIsLocked` (editor lock, read-only
   * adapter, archived required schemas), so a read-only table can't be edited through pivot mode.
   * Protection is excluded so fill-in-only entries keep editable value cells, matching the normal view.
   */
  locked: boolean;
  /**
   * The already-evaluated canonical table, reused from the normal render path (`itemProps.computedTable`)
   * so entering pivot mode doesn't re-evaluate the whole table.
   */
  computedTable: Array<Array<EvaluatedCell>>;
  /**
   * Server/mismatch validation errors for the canonical table, threaded into hydration so error
   * decorations render the same as the normal view.
   */
  validationErrors: Array<TableValidationCellError>;
  /** Receives an edited copy of the table. */
  onUpdateStructuredTable: (next: UnknownStructuredTable) => void;
  /** Undo-history callbacks, so Ctrl/Cmd+Z works the same as the normal view. */
  storeState: TableEditableProps['storeState'];
  restoreState: TableEditableProps['restoreState'];
}

const DEFAULT_PROJECTED_COLUMN_WIDTH = 160;

// The frozen row-label column is sized to fit the widest row label, bounded so it neither collapses
// on short labels nor eats the viewport on long ones. Padding adds horizontal room around the
// measured text width.
const ROW_LABEL_MIN_WIDTH = 140;
const ROW_LABEL_MAX_WIDTH = 260;
const ROW_LABEL_PADDING = 24;
/** Approximate row-label font size in px; used only to estimate text width. */
const ROW_LABEL_FONT_SIZE = 13;

/** A synthetic column: one transposed column, keyed by the spec's classification. */
interface TransposedColumn {
  key: string;
  /**
   * Stable provenanceUuid used to key this column's per-row delegated constraint and its persisted width.
   * Derived from the spec's (run-stable) `columnKey`, so widths survive across runs of the same table.
   */
  synthUuid: string;
  label: string;
}

/** A synthetic row: one transposed row spanning all of its columns. */
interface TransposedRow {
  rowKey: string;
  representativeRowIndex: number;
  label: string;
  /** column key -> canonical row index (absent = no canonical row for that cell). */
  rowIndexByColumnKey: Map<string, number>;
}

export default function TransposedStructuredTableEditable(
  props: TransposedStructuredTableEditableProps
): JSX.Element {
  const {
    structuredTable,
    pivotSpec,
    locked,
    computedTable,
    validationErrors,
    onUpdateStructuredTable,
    storeState,
    restoreState,
  } = props;
  const adapter = getStructuredTableAdapters()[structuredTable.kind];
  const {classes} = useStyles();
  const theme = useMantineTheme();

  // Hydrate the canonical table from the already-evaluated `computedTable`; this is our source of cells
  // and per-row delegated constraints. The engine independently re-evaluates the synthetic table below.
  const {table: canonicalTable} = useHydratedStructuredTableData({
    adapter,
    structuredTable,
    computedTable,
    validationErrors,
    // No mismatch resolution action: mismatch errors render, but are resolved in the normal view.
    options: {hasPrimarySamples: pivotSpec.hasPrimarySamples ?? true},
  });

  // Persist user-adjusted column widths ourselves: STON's resize plumbing can't be reused because pivot
  // mode feeds the engine a synthetic Table directly. The spec picks the storage-key namespace (parameter
  // tables share one per unit operation and mode); we fall back to this table's provenanceUuid.
  const columnWidthStoreKey =
    pivotSpec.getColumnWidthStoreKey?.(canonicalTable) ?? structuredTable.provenanceUuid;
  const {widthsByColumnId: columnWidthsBySynthUuid, updateWidths} = useLocalStorageColumnWidthStore(
    `notebook.pivotMode.columnWidths.${columnWidthStoreKey}`
  );

  const projectedColIndex = useMemo(
    () => columnIndexByUuid(canonicalTable).get(pivotSpec.projectedColumnUuid),
    [canonicalTable, pivotSpec.projectedColumnUuid]
  );

  // Build the transposed axes: ordered columns and ordered rows, plus the (row, column) ->
  // canonical-row-index lookup used for cell resolution and write-back.
  const {transposedColumns, transposedRows} = useMemo(() => {
    const classify = pivotSpec.createRowClassifier(canonicalTable);
    const columns: Array<TransposedColumn> = [];
    const seenColumnKeys = new Set<string>();
    const rowByKey = new Map<string, TransposedRow>();
    const rowOrder: Array<string> = [];

    (canonicalTable.rowData ?? []).forEach((_rowData, rowIndex) => {
      const classification = classify(rowIndex);

      if (!seenColumnKeys.has(classification.columnKey)) {
        seenColumnKeys.add(classification.columnKey);
        columns.push({
          key: classification.columnKey,
          synthUuid: `projected::${classification.columnKey}`,
          label: classification.columnLabel,
        });
      }

      let row = rowByKey.get(classification.rowKey);
      if (row == null) {
        row = {
          rowKey: classification.rowKey,
          representativeRowIndex: rowIndex,
          label: classification.rowLabel,
          rowIndexByColumnKey: new Map(),
        };
        rowByKey.set(classification.rowKey, row);
        rowOrder.push(classification.rowKey);
      }
      row.rowIndexByColumnKey.set(classification.columnKey, rowIndex);
    });

    return {
      transposedColumns: columns,
      transposedRows: rowOrder.map((key) => rowByKey.get(key)!),
    };
  }, [canonicalTable, pivotSpec]);

  // Size the frozen row-label column to the widest parameter name, clamped to sane bounds. Measured
  // via a cached canvas so it stays in sync with the label text without a DOM render/reflow pass.
  const rowLabelWidth = useMemo(() => {
    const fontFamily = theme.fontFamily ?? 'sans-serif';
    const widest = transposedRows.reduce((max, row) => {
      const {width} = getTextDimensions(row.label, 'normal', ROW_LABEL_FONT_SIZE, fontFamily);
      return Math.max(max, width);
    }, 0);
    return Math.min(
      ROW_LABEL_MAX_WIDTH,
      Math.max(ROW_LABEL_MIN_WIDTH, Math.ceil(widest) + ROW_LABEL_PADDING)
    );
  }, [transposedRows, theme.fontFamily]);

  // Assemble the synthetic pivoted Table fed to the engine. Start from the canonical table so all
  // required TableData/TableViewProps fields are present, then override the pivoted axes.
  const syntheticTable = useMemo<Table>(() => {
    const baseProjectedConstraint =
      projectedColIndex == null ? null : (canonicalTable.columnConstraints[projectedColIndex] ?? null);

    const columnConstraints: Array<ColumnConstraint | null> = transposedColumns.map((column) => {
      if (baseProjectedConstraint == null) {
        return null;
      }
      return {
        ...baseProjectedConstraint,
        renderedLabel: column.label,
        structuredTableColumn: baseProjectedConstraint.structuredTableColumn
          ? {...baseProjectedConstraint.structuredTableColumn, provenanceUuid: column.synthUuid}
          : baseProjectedConstraint.structuredTableColumn,
      };
    });

    const columnLabels: Array<string | null> = transposedColumns.map((column) => column.label);

    const widths: Array<number> = transposedColumns.map((column) => {
      const baseWidth =
        (projectedColIndex == null
          ? DEFAULT_PROJECTED_COLUMN_WIDTH
          : canonicalTable.widths[projectedColIndex]) ?? DEFAULT_PROJECTED_COLUMN_WIDTH;
      // A width the user dragged for this column (keyed run-stably) wins over the projected default.
      return columnWidthsBySynthUuid[column.synthUuid] ?? baseWidth;
    });

    const cells: Array<Array<Cell>> = transposedRows.map((transposedRow) =>
      transposedColumns.map((column) => {
        const canonRow = transposedRow.rowIndexByColumnKey.get(column.key);
        if (canonRow == null || projectedColIndex == null) {
          // No canonical row for this cell: an inert, non-editable gap in the sparse matrix.
          return {text: '', locked: true};
        }
        return canonicalTable.table[canonRow]?.[projectedColIndex] ?? {text: '', locked: true};
      })
    );

    const rowData: Array<RowData> = transposedRows.map((transposedRow) => {
      const representative = canonicalTable.rowData?.[transposedRow.representativeRowIndex];
      const delegatedColumnConstraints = new Map<string, ColumnConstraint>();
      transposedColumns.forEach((column) => {
        const canonRow = transposedRow.rowIndexByColumnKey.get(column.key);
        const concrete =
          canonRow == null
            ? null
            : (canonicalTable.rowData?.[canonRow]?.delegatedColumnConstraints?.get(
                pivotSpec.projectedColumnUuid
              ) ?? null);
        if (concrete != null) {
          delegatedColumnConstraints.set(column.synthUuid, concrete);
        }
      });
      return {...representative, delegatedColumnConstraints} as RowData;
    });

    return {
      ...canonicalTable,
      name: `${canonicalTable.name} (pivot)`,
      columnConstraints,
      columnLabels,
      widths,
      table: cells,
      rowData,
      rowLabels: transposedRows.map((transposedRow) => transposedRow.label),
      // Widen the frozen row-label column so labels are readable (sized to the widest label).
      _firstColumnWidth: rowLabelWidth,
      // The table's frozen columns don't exist in the pivot, where the row labels are the frozen axis.
      frozenColumnCount: 0,
      headerRow: false,
      headerCol: false,
      allowColumnEdits: false,
      allowRowRemoval: false,
      allowAddRows: false,
      lockedDims: true,
    };
  }, [
    canonicalTable,
    transposedColumns,
    transposedRows,
    projectedColIndex,
    pivotSpec.projectedColumnUuid,
    rowLabelWidth,
    columnWidthsBySynthUuid,
  ]);

  // Keep the axis maps and last-rendered widths current for the write-back / resize handlers without
  // re-binding them every render.
  const pivotRef = useRef({
    transposedColumns,
    transposedRows,
    projectedColIndex,
    displayedWidths: syntheticTable.widths,
  });
  pivotRef.current = {
    transposedColumns,
    transposedRows,
    projectedColIndex,
    displayedWidths: syntheticTable.widths,
  };

  // A resize arrives as a widths-only update. Divert it to the width store (keyed by each column's
  // synthUuid) instead of the canonical table, so widths persist without dirtying the table's values.
  const captureColumnWidths = (nextSynthetic: Table): void => {
    if (locked) {
      return;
    }
    const {transposedColumns, displayedWidths} = pivotRef.current;
    const changed: Record<string, number> = {};
    transposedColumns.forEach((column, index) => {
      const nextWidth = nextSynthetic.widths[index];
      if (nextWidth != null && nextWidth !== displayedWidths[index]) {
        changed[column.synthUuid] = nextWidth;
      }
    });
    if (Object.keys(changed).length > 0) {
      updateWidths((current) => ({...current, ...changed}));
    }
  };

  const commitSyntheticTable = (nextSynthetic: Table): void => {
    const {transposedColumns, transposedRows, projectedColIndex} = pivotRef.current;
    // Read-only tables never write back, even if the engine somehow surfaces an edit.
    if (locked || projectedColIndex == null) {
      return;
    }
    const next = produce(structuredTable, (draft) => {
      nextSynthetic.table.forEach((row, rowIdx) => {
        const transposedRow = transposedRows[rowIdx];
        if (transposedRow == null) {
          return;
        }
        row.forEach((cell, colIdx) => {
          const canonRow = transposedRow.rowIndexByColumnKey.get(transposedColumns[colIdx]?.key);
          if (canonRow == null) {
            return;
          }
          const target = draft.rows[canonRow]?.cells[projectedColIndex];
          if (target == null) {
            return;
          }
          // Persist value only; never carry hydrated UI fields (error/indicator JSX) back to the model.
          // Preserve a blank cell's absent typedValue rather than coercing to null, so a widths-only
          // update (which replays every cell) doesn't dirty untouched blank cells.
          if (cell.typedValue != null) {
            target.typedValue = cell.typedValue;
          } else {
            delete target.typedValue;
          }
          target.text = cell.text ?? '';
        });
      });
    });
    // `produce` returns the same reference when nothing changed. A pure resize writes no cell (and is
    // already diverted to the width store), so skip the persist to avoid dirtying the notebook item.
    if (next !== structuredTable) {
      onUpdateStructuredTable(next as unknown as UnknownStructuredTable);
    }
  };

  const structuredTableContextValue = useMemo(
    () => ({
      structuredTableKind: structuredTable.kind,
      provenanceUuid: structuredTable.provenanceUuid,
    }),
    [structuredTable.kind, structuredTable.provenanceUuid]
  );

  return (
    <Box
      className={classes.root}
      {...getTestProps({component: 'TransposedStructuredTableEditable', element: 'root'})}
    >
      <Box className={classes.scrollArea}>
        <MediocreWrapper>
          <StructuredTableContext.Provider value={structuredTableContextValue}>
            <SimpleTableEditable
              {...standaloneProps}
              cid={`${structuredTable.cid}--transposed`}
              table={syntheticTable}
              tableLabel={adapter.getDisplayName(structuredTable)}
              enableColumnResizing={!locked}
              updateHandler={(_index: number, {table}: {table: Table}) => {
                // Capture any resize to the width store, then run the write-back (no-ops for a pure resize).
                captureColumnWidths(table);
                commitSyntheticTable(table);
              }}
              // The engine consumes the Ctrl/Cmd+Z keypress, so a no-op here would disable undo/redo.
              // The synthetic table re-derives from the restored canonical prop.
              storeState={storeState}
              restoreState={restoreState}
              // standaloneProps stubs this with an asserting UNSUPPORTED_OPERATION; the engine calls it
              // during cell navigation, so provide a no-op like STON does.
              tabHandler={noop}
              insertItemHandler={noop}
              locked={locked}
              enableFormulas={false}
              enableFillDown={true}
              canAddRemoveColumns={false}
              canAddColumnsFreeform={false}
              allowReorderDimensions={{[TableDimension.ROW]: false, [TableDimension.COLUMN]: false}}
              hideFormulaBox={true}
              isCollapsible={false}
              isCompactBottomSpacing={false}
              isLeftAlignColumnHeader={true}
              isLeftAlignRowHeader={true}
              suppressTitleBar={true}
              disableMaximizedTables={true}
              setMaximizedTable={noop}
              setSplitScreenTable={noop}
              isTableNameUsed={() => false}
              icon={adapter.getTableIcon(structuredTable)}
            />
          </StructuredTableContext.Provider>
        </MediocreWrapper>
      </Box>
    </Box>
  );
}

const useStyles = createStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: theme.white,
    overflow: 'hidden',
  },
  scrollArea: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing.md,
  },
}));
